import {
  analyzeSql,
  ENVIRONMENT_TOKENS,
  explainUnsupportedReason,
  guessTableFromSql,
  type ApiErrorBody,
  type DbInsightsActivityDto,
  type DbInsightsIdleInTransactionDto,
  type DbInsightsLockDto,
  type DbInsightsStatementDto,
} from '@visualitics-toolkit/shared';
import { useEffect, useState, type ReactNode } from 'react';

import { Badge, Button, Checkbox, CopyButton, EmptyState, Input, Textarea } from '../components/atoms';
import {
  ConfirmTokenField,
  ErrorBlock,
  LoadingBlock,
  formatDateTime,
  formatDuration,
} from '../components/molecules';
import { Dialog } from '../components/organisms/Dialog';
import {
  useActiveConnection,
  useDbInsightsCancelBackend,
  useDbInsightsExplain,
  useDbInsightsIndexes,
  useDbInsightsSnapshot,
  useDbInsightsTerminateBackend,
} from '../lib/queries';
import { ApiError } from '../lib/api-client';

type BackendAction = {
  action: 'cancel' | 'terminate';
  pid: number;
  label: string;
  queryPreview: string;
};

export function DbInsightsPage(): ReactNode {
  const connection = useActiveConnection();
  const snapshot = useDbInsightsSnapshot(connection?.id);
  const [investigateSql, setInvestigateSql] = useState<string | null>(null);
  const [backendAction, setBackendAction] = useState<BackendAction | null>(null);

  if (!connection) {
    return (
      <EmptyState
        title="Nenhuma conexão ativa"
        description="Ative uma conexão para acompanhar as métricas do banco alvo."
      />
    );
  }

  const data = snapshot.data;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-content">Insights do banco</h1>
            <Badge tone={data?.statementsAvailable === false ? 'warning' : 'accent'}>
              {connection.name}
            </Badge>
          </div>
          <p className="text-sm text-muted">
            Live via `pg_stat_activity` / locks; rankings via `pg_stat_statements`; investigar com
            EXPLAIN (ANALYZE, BUFFERS) e índices.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {data && (
            <span className="text-xs text-muted">Atualizado em {formatDateTime(data.capturedAt)}</span>
          )}
          <Button onClick={() => void snapshot.refetch()} loading={snapshot.isFetching && !snapshot.isLoading}>
            Atualizar
          </Button>
        </div>
      </header>

      {snapshot.isLoading && <LoadingBlock label="Consultando métricas do banco…" />}
      {snapshot.error && <ErrorBlock message={readError(snapshot.error)} />}

      {data && (
        <>
          {!data.statementsAvailable && (
            <ErrorBlock
              message={
                data.statementsHint ??
                '`pg_stat_statements` não está habilitado nesta conexão. Os rankings de queries ficam vazios até a extensão ser ativada.'
              }
            />
          )}

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-7">
            <MetricCard label="Conexões totais" value={data.connections.total} />
            <MetricCard label="Ativas" value={data.connections.active} tone="accent" />
            <MetricCard label="Idle" value={data.connections.idle} />
            <MetricCard
              label="Idle em transação"
              value={data.connections.idleInTransaction}
              tone={data.connections.idleInTransaction > 0 ? 'warning' : 'neutral'}
            />
            <MetricCard
              label="Locks ativos"
              value={data.locks.length}
              tone={data.locks.length > 0 ? 'warning' : 'neutral'}
            />
            <MetricCard
              label="Queries ≥ 5s"
              value={data.slowQueries.length}
              tone={data.slowQueries.length > 0 ? 'warning' : 'neutral'}
            />
            <MetricCard label="Max connections" value={data.connections.max ?? '—'} />
          </section>

          <section className="card space-y-3">
            <SectionHeader
              title="Queries ≥ 5 segundos"
              description="Sessões ativas com duração ≥ 5s (exclui application_name do toolkit)."
              count={data.slowQueries.length}
              tone={data.slowQueries.length > 0 ? 'warning' : 'accent'}
            />
            <ActivityTable
              rows={data.slowQueries}
              onInvestigate={setInvestigateSql}
              onCancel={(row) =>
                setBackendAction({
                  action: 'cancel',
                  pid: row.pid,
                  label: `Cancelar PID ${row.pid}`,
                  queryPreview: row.query,
                })
              }
              onTerminate={(row) =>
                setBackendAction({
                  action: 'terminate',
                  pid: row.pid,
                  label: `Matar sessão PID ${row.pid}`,
                  queryPreview: row.query,
                })
              }
            />
          </section>

          <section className="card space-y-3">
            <SectionHeader
              title="Locks (bloqueador → bloqueado)"
              description="Quem segura o lock e quem espera. Preferência: cancelar/matar o bloqueador."
              count={data.locks.length}
              tone={data.locks.length > 0 ? 'warning' : 'accent'}
            />
            <LocksTable
              rows={data.locks}
              onInvestigate={setInvestigateSql}
              onCancel={(pid, query) =>
                setBackendAction({
                  action: 'cancel',
                  pid,
                  label: `Cancelar PID ${pid}`,
                  queryPreview: query,
                })
              }
              onTerminate={(pid, query) =>
                setBackendAction({
                  action: 'terminate',
                  pid,
                  label: `Matar sessão PID ${pid}`,
                  queryPreview: query,
                })
              }
            />
          </section>

          <section className="card space-y-3">
            <SectionHeader
              title="Idle in transaction"
              description="Transações abertas sem statement ativo — causa frequente de locks silenciosos."
              count={data.idleInTransaction.length}
              tone={data.idleInTransaction.length > 0 ? 'warning' : 'accent'}
            />
            <IdleTable
              rows={data.idleInTransaction}
              onTerminate={(row) =>
                setBackendAction({
                  action: 'terminate',
                  pid: row.pid,
                  label: `Matar idle-in-xact PID ${row.pid}`,
                  queryPreview: row.query,
                })
              }
            />
          </section>

          <section className="card space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-content">Últimas queries em execução</h2>
                <p className="text-sm text-muted">
                  Top 15 por `query_start`, excluindo sessões idle e a própria sessão do toolkit.
                </p>
              </div>
              <Badge tone="accent">{data.recentQueries.length}</Badge>
            </div>
            <ActivityTable rows={data.recentQueries} onInvestigate={setInvestigateSql} />
          </section>

          <section className="grid gap-4 xl:grid-cols-2">
            <section className="card space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-base font-semibold text-content">Queries mais pesadas</h2>
                  <p className="text-sm text-muted">
                    Top 15 por `total_exec_time`, com blocos lidos para indicar custo acumulado.
                  </p>
                </div>
                <WindowBadge startedAt={data.statementsWindowStartedAt} />
              </div>
              <StatementTable rows={data.heaviestQueries} metric="total" onInvestigate={setInvestigateSql} />
            </section>

            <section className="card space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-base font-semibold text-content">Queries mais demoradas</h2>
                  <p className="text-sm text-muted">
                    Top 15 por `mean_exec_time`, útil para detectar statements lentos mesmo com poucas
                    chamadas.
                  </p>
                </div>
                <WindowBadge startedAt={data.statementsWindowStartedAt} />
              </div>
              <StatementTable rows={data.slowestQueries} metric="mean" onInvestigate={setInvestigateSql} />
            </section>
          </section>
        </>
      )}

      {investigateSql !== null && (
        <InvestigateDialog
          connectionId={connection.id}
          initialSql={investigateSql}
          onClose={() => setInvestigateSql(null)}
        />
      )}

      {backendAction && (
        <BackendActionDialog
          connectionId={connection.id}
          environment={connection.environment}
          action={backendAction}
          onClose={() => setBackendAction(null)}
        />
      )}
    </div>
  );
}

function InvestigateDialog({
  connectionId,
  initialSql,
  onClose,
}: {
  connectionId: string;
  initialSql: string;
  onClose: () => void;
}): ReactNode {
  const [sql, setSql] = useState(initialSql === '—' ? '' : initialSql);
  const analysis = analyzeSql(sql.trim() ? sql : 'SELECT 1');
  const explainBlockReason = sql.trim() ? explainUnsupportedReason(sql) : 'Informe um statement SQL.';
  const canExplain = explainBlockReason === null;
  const canAnalyze = canExplain && analysis.isReadOnly;
  const [analyze, setAnalyze] = useState(canAnalyze);
  const [table, setTable] = useState(() => guessTableFromSql(initialSql) ?? '');
  const [schema, setSchema] = useState('public');
  const explain = useDbInsightsExplain(connectionId);
  const indexes = useDbInsightsIndexes(connectionId);

  useEffect(() => {
    if (!canAnalyze) setAnalyze(false);
  }, [canAnalyze]);

  const plan = explain.data;
  const seqScanWarning =
    plan &&
    plan.flags.hasSeqScan &&
    !plan.flags.hasIndexScan &&
    !plan.flags.hasBitmapIndexScan;

  return (
    <Dialog title="Investigar query" onClose={onClose} wide>
      <div className="space-y-4">
        <label className="block space-y-1">
          <span className="text-xs text-muted">SQL</span>
          <Textarea
            rows={6}
            value={sql}
            onChange={(event) => setSql(event.target.value)}
            className="font-mono text-xs"
          />
        </label>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <Checkbox
              checked={analyze}
              disabled={!canAnalyze}
              onChange={(event) => setAnalyze(event.target.checked)}
              label="Rodar ANALYZE (reexecuta SELECT)"
            />
            <Button
              variant="primary"
              loading={explain.isPending}
              disabled={!canExplain}
              onClick={() => explain.mutate({ sql, analyze })}
            >
              {analyze ? 'EXPLAIN (ANALYZE, BUFFERS)' : 'EXPLAIN (BUFFERS)'}
            </Button>
          </div>
          {!canAnalyze && (
            <p className="text-xs text-muted">
              {canExplain
                ? 'ANALYZE desligado: só SELECT/WITH read-only. Este statement roda EXPLAIN (BUFFERS) sem reexecutar escrita.'
                : 'ANALYZE indisponível neste SQL (veja o aviso abaixo).'}
            </p>
          )}
          {explainBlockReason && <ErrorBlock message={explainBlockReason} />}
        </div>

        {explain.error && <ErrorBlock message={readError(explain.error)} />}
        {plan?.analyzeForcedOff && (
          <ErrorBlock message="ANALYZE foi desligado: o SQL não é read-only. Rodado EXPLAIN (BUFFERS) apenas." />
        )}

        {plan && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={seqScanWarning ? 'warning' : 'accent'}>
                {seqScanWarning
                  ? 'Seq Scan sem Index/Bitmap — forte indício de falta de índice'
                  : 'Plano'}
              </Badge>
              <span className="text-xs text-muted">
                {plan.analyze ? 'ANALYZE on' : 'ANALYZE off'} · {formatDuration(plan.durationMs)} ·
                timeout {formatDuration(plan.statementTimeoutMs)}
              </span>
              <CopyButton value={plan.planText} label="Copiar plano" />
            </div>
            <pre className="max-h-80 overflow-auto rounded-md border border-border bg-surface p-3 font-mono text-xs text-content whitespace-pre-wrap">
              {plan.planText}
            </pre>
          </div>
        )}

        <div className="border-t border-border pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-content">Índices (`pg_indexes`)</h3>
          <div className="flex flex-wrap items-end gap-2">
            <label className="space-y-1">
              <span className="text-xs text-muted">Schema</span>
              <Input value={schema} onChange={(event) => setSchema(event.target.value)} />
            </label>
            <label className="flex-1 space-y-1">
              <span className="text-xs text-muted">Tabela</span>
              <Input value={table} onChange={(event) => setTable(event.target.value)} />
            </label>
            <Button
              loading={indexes.isPending}
              disabled={!table.trim()}
              onClick={() => indexes.mutate({ table: table.trim(), schema: schema.trim() || 'public' })}
            >
              Ver índices
            </Button>
          </div>
          {indexes.error && <ErrorBlock message={readError(indexes.error)} />}
          {indexes.data && (
            <div className="space-y-2">
              {indexes.data.indexes.length === 0 ? (
                <EmptyState
                  title="Nenhum índice"
                  description={`Não há índices em ${indexes.data.schema}.${indexes.data.tableName}.`}
                />
              ) : (
                indexes.data.indexes.map((entry) => (
                  <article
                    key={entry.indexName}
                    className="rounded-md border border-border bg-surface px-3 py-2 space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm text-content">{entry.indexName}</span>
                      <CopyButton value={entry.indexDef} />
                    </div>
                    <code className="block whitespace-pre-wrap break-words font-mono text-xs text-muted">
                      {entry.indexDef}
                    </code>
                  </article>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </Dialog>
  );
}

function BackendActionDialog({
  connectionId,
  environment,
  action,
  onClose,
}: {
  connectionId: string;
  environment: keyof typeof ENVIRONMENT_TOKENS;
  action: BackendAction;
  onClose: () => void;
}): ReactNode {
  const [confirmToken, setConfirmToken] = useState('');
  const expected = ENVIRONMENT_TOKENS[environment];
  const cancel = useDbInsightsCancelBackend(connectionId);
  const terminate = useDbInsightsTerminateBackend(connectionId);
  const mutation = action.action === 'cancel' ? cancel : terminate;
  const matched = confirmToken.trim() === expected;

  return (
    <Dialog title={action.label} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-muted">
          {action.action === 'cancel'
            ? 'Cancela o statement em execução (`pg_cancel_backend`).'
            : 'Encerra a sessão (`pg_terminate_backend`) — rollback da transação e drop da conexão.'}
        </p>
        <code className="block whitespace-pre-wrap break-words rounded-md border border-border bg-surface p-2 font-mono text-xs text-content">
          {action.queryPreview}
        </code>
        <ConfirmTokenField
          kind="environment"
          hint={`Digite o ambiente (${expected}) para confirmar`}
          value={confirmToken}
          onChange={setConfirmToken}
          expected={expected}
        />
        {mutation.error && <ErrorBlock message={readError(mutation.error)} />}
        {mutation.data && (
          <p className="text-sm text-content">
            {mutation.data.succeeded
              ? `Ação ${mutation.data.action} aplicada no PID ${mutation.data.pid}.`
              : `Postgres retornou false para PID ${mutation.data.pid} (sessão pode ter sumido).`}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Fechar</Button>
          <Button
            variant="danger"
            loading={mutation.isPending}
            disabled={!matched || Boolean(mutation.data)}
            onClick={() =>
              mutation.mutate(
                { pid: action.pid, confirmToken },
                { onSuccess: () => undefined },
              )
            }
          >
            Confirmar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function SectionHeader({
  title,
  description,
  count,
  tone,
}: {
  title: string;
  description: string;
  count: number;
  tone: 'accent' | 'warning';
}): ReactNode {
  return (
    <div className="flex items-start justify-between gap-2">
      <div>
        <h2 className="text-base font-semibold text-content">{title}</h2>
        <p className="text-sm text-muted">{description}</p>
      </div>
      <Badge tone={tone}>{count}</Badge>
    </div>
  );
}

function MetricCard({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'accent' | 'warning';
}): ReactNode {
  const toneClass =
    tone === 'accent'
      ? 'border-accent/50'
      : tone === 'warning'
        ? 'border-severity-destructive/50'
        : 'border-border';

  return (
    <div className={`card space-y-1 ${toneClass}`}>
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="font-mono text-2xl text-content">{value}</p>
    </div>
  );
}

function WindowBadge({ startedAt }: { startedAt: string | null }): ReactNode {
  if (!startedAt) return <Badge tone="warning">sem janela</Badge>;
  return <Badge tone="accent">desde reset · {formatAge(startedAt)}</Badge>;
}

function ActivityTable({
  rows,
  onInvestigate,
  onCancel,
  onTerminate,
}: {
  rows: DbInsightsActivityDto[];
  onInvestigate?: (sql: string) => void;
  onCancel?: (row: DbInsightsActivityDto) => void;
  onTerminate?: (row: DbInsightsActivityDto) => void;
}): ReactNode {
  if (rows.length === 0) {
    return <EmptyState title="Nenhuma query" description="Sem sessões neste filtro no momento." />;
  }

  const showActions = Boolean(onInvestigate || onCancel || onTerminate);

  return (
    <div className="max-h-[32rem] overflow-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-surface-raised text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            <Th>Estado</Th>
            <Th>Duração</Th>
            <Th>Início</Th>
            <Th>Origem</Th>
            <Th>Query</Th>
            {showActions && <Th>Ações</Th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.pid} className="border-t border-border/60 align-top">
              <Td>
                <div className="space-y-1">
                  <div className="font-medium text-content">{row.state ?? '—'}</div>
                  <div className="text-xs text-muted">PID {row.pid}</div>
                  {(row.durationMs ?? 0) >= 5000 && <Badge tone="warning">≥5s</Badge>}
                </div>
              </Td>
              <Td>{formatDuration(row.durationMs)}</Td>
              <Td>{formatDateTime(row.queryStart)}</Td>
              <Td>
                <div className="space-y-1 text-xs text-muted">
                  <div>{row.userName ?? '—'}</div>
                  <div>{row.applicationName ?? '—'}</div>
                  <div>{row.clientAddress ?? 'local'}</div>
                </div>
              </Td>
              <Td>
                <code className="block whitespace-pre-wrap break-words font-mono text-xs text-content">
                  {row.query}
                </code>
                {(row.waitEventType || row.waitEvent) && (
                  <p className="mt-1 text-xs text-muted">
                    espera: {[row.waitEventType, row.waitEvent].filter(Boolean).join(' / ')}
                  </p>
                )}
              </Td>
              {showActions && (
                <Td>
                  <div className="flex flex-col gap-1">
                    {onInvestigate && (
                      <Button className="px-2 py-1 text-xs" onClick={() => onInvestigate(row.query)}>
                        Investigar
                      </Button>
                    )}
                    {onCancel && (
                      <Button className="px-2 py-1 text-xs" onClick={() => onCancel(row)}>
                        Cancelar
                      </Button>
                    )}
                    {onTerminate && (
                      <Button
                        variant="danger"
                        className="px-2 py-1 text-xs"
                        onClick={() => onTerminate(row)}
                      >
                        Matar
                      </Button>
                    )}
                  </div>
                </Td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LocksTable({
  rows,
  onInvestigate,
  onCancel,
  onTerminate,
}: {
  rows: DbInsightsLockDto[];
  onInvestigate: (sql: string) => void;
  onCancel: (pid: number, query: string) => void;
  onTerminate: (pid: number, query: string) => void;
}): ReactNode {
  if (rows.length === 0) {
    return <EmptyState title="Sem locks" description="Nenhuma espera de lock no momento." />;
  }

  return (
    <div className="max-h-[32rem] overflow-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-surface-raised text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            <Th>Bloqueado</Th>
            <Th>Bloqueador</Th>
            <Th>Lock</Th>
            <Th>Ações</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.blockedPid}-${row.blockingPid}-${row.lockMode}-${row.relation}`}
              className="border-t border-border/60 align-top"
            >
              <Td>
                <div className="space-y-1">
                  <div className="font-medium text-content">PID {row.blockedPid}</div>
                  <div className="text-xs text-muted">
                    {row.blockedApp ?? '—'} · {row.blockedState ?? '—'} ·{' '}
                    {formatDuration(row.blockedDurationMs)}
                  </div>
                  <code className="block whitespace-pre-wrap break-words font-mono text-xs text-content">
                    {row.blockedQuery}
                  </code>
                </div>
              </Td>
              <Td>
                <div className="space-y-1">
                  <div className="font-medium text-content">PID {row.blockingPid}</div>
                  <div className="text-xs text-muted">
                    {row.blockingApp ?? '—'} · {row.blockingState ?? '—'} · held{' '}
                    {formatDuration(row.blockingHeldMs)}
                  </div>
                  <code className="block whitespace-pre-wrap break-words font-mono text-xs text-content">
                    {row.blockingQuery}
                  </code>
                </div>
              </Td>
              <Td>
                <div className="space-y-1 text-xs text-muted">
                  <div>{row.lockType ?? '—'}</div>
                  <div>{row.lockMode ?? '—'}</div>
                  <div className="font-mono text-content">{row.relation ?? '—'}</div>
                </div>
              </Td>
              <Td>
                <div className="flex flex-col gap-1">
                  <Button
                    className="px-2 py-1 text-xs"
                    onClick={() => onInvestigate(row.blockingQuery)}
                  >
                    Investigar bloqueador
                  </Button>
                  <Button
                    className="px-2 py-1 text-xs"
                    onClick={() => onCancel(row.blockingPid, row.blockingQuery)}
                  >
                    Cancelar bloqueador
                  </Button>
                  <Button
                    variant="danger"
                    className="px-2 py-1 text-xs"
                    onClick={() => onTerminate(row.blockingPid, row.blockingQuery)}
                  >
                    Matar bloqueador
                  </Button>
                  <Button
                    className="px-2 py-1 text-xs"
                    onClick={() => onCancel(row.blockedPid, row.blockedQuery)}
                  >
                    Cancelar bloqueado
                  </Button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IdleTable({
  rows,
  onTerminate,
}: {
  rows: DbInsightsIdleInTransactionDto[];
  onTerminate: (row: DbInsightsIdleInTransactionDto) => void;
}): ReactNode {
  if (rows.length === 0) {
    return (
      <EmptyState title="Nenhum idle in transaction" description="Nenhuma sessão nesta condição." />
    );
  }

  return (
    <div className="max-h-[24rem] overflow-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-surface-raised text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            <Th>PID</Th>
            <Th>Idade da xact</Th>
            <Th>Origem</Th>
            <Th>Última query</Th>
            <Th>Ações</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.pid} className="border-t border-border/60 align-top">
              <Td className="font-mono">{row.pid}</Td>
              <Td>{formatDuration(row.xactAgeMs)}</Td>
              <Td>
                <div className="space-y-1 text-xs text-muted">
                  <div>{row.userName ?? '—'}</div>
                  <div>{row.applicationName ?? '—'}</div>
                  <div>{row.clientAddress ?? 'local'}</div>
                </div>
              </Td>
              <Td>
                <code className="block whitespace-pre-wrap break-words font-mono text-xs text-content">
                  {row.query}
                </code>
              </Td>
              <Td>
                <Button variant="danger" className="px-2 py-1 text-xs" onClick={() => onTerminate(row)}>
                  Matar
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatementTable({
  rows,
  metric,
  onInvestigate,
}: {
  rows: DbInsightsStatementDto[];
  metric: 'total' | 'mean';
  onInvestigate: (sql: string) => void;
}): ReactNode {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Sem dados de ranking"
        description="A extensão `pg_stat_statements` pode não estar ativa ou ainda não acumulou amostras."
      />
    );
  }

  return (
    <div className="max-h-[32rem] space-y-3 overflow-y-auto pr-1">
      {rows.map((row, index) => {
        const reads = (row.sharedBlksRead ?? 0) + (row.tempBlksRead ?? 0);
        const metricValue = metric === 'total' ? row.totalExecTimeMs : row.meanExecTimeMs;
        return (
          <article
            key={`${row.queryId ?? row.query}-${index}`}
            className="rounded-md border border-border bg-surface px-3 py-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge tone={metric === 'total' ? 'warning' : 'accent'}>#{index + 1}</Badge>
                <span className="text-sm text-muted">
                  {metric === 'total' ? 'tempo total' : 'tempo médio'}:{' '}
                  <span className="font-mono text-content">{formatDuration(metricValue)}</span>
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <span>calls {row.calls}</span>
                <span>max {formatDuration(row.maxExecTimeMs)}</span>
                <span>rows {row.rows}</span>
                <span>reads {reads}</span>
                <Button className="px-2 py-1 text-xs" onClick={() => onInvestigate(row.query)}>
                  Investigar
                </Button>
              </div>
            </div>
            <code className="mt-2 block whitespace-pre-wrap break-words font-mono text-xs text-content">
              {row.query}
            </code>
          </article>
        );
      })}
    </div>
  );
}

function Th({ children }: { children: ReactNode }): ReactNode {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, className }: { children: ReactNode; className?: string }): ReactNode {
  return <td className={`px-3 py-2 ${className ?? ''}`}>{children}</td>;
}

function formatAge(iso: string): string {
  const delta = Math.max(0, Date.now() - new Date(iso).getTime());
  const minutes = Math.round(delta / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

function readError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  const body = error as Partial<ApiErrorBody> | undefined;
  if (body?.message) return body.message;
  return 'Não foi possível carregar os insights do banco.';
}
